import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import StorySubmissionForm from '@/app/homepage/components/StorySubmissionForm';

interface Story {
  id: number;
  title: string;
  location: string;
  image: string;
  alt: string;
  impact: string;
  participants: string;
  description: string;
  category: string;
}

const mockStories: Story[] = [
{
  id: 1,
  title: 'Healing Divided Communities in Belfast',
  location: 'Belfast, Northern Ireland',
  image: "https://images.unsplash.com/photo-1630569267529-633c7644ee7d",
  alt: 'Diverse group of people holding hands in circle outdoors with green grass and blue sky',
  impact: '89% reduction in neighborhood tensions',
  participants: '2,400 residents',
  description: 'Former adversaries came together through the 30-day journey, transforming decades of sectarian division into collaborative community projects.',
  category: 'Community Reconciliation'
},
{
  id: 2,
  title: 'School Peace Initiative in Dar es Salaam',
  location: 'Dar es Salaam, Tanzania',
  image: "https://images.unsplash.com/photo-1727553957790-3f8f7a0f5614",
  alt: 'African children in colorful school uniforms smiling and raising hands in classroom',
  impact: '94% decrease in bullying incidents',
  participants: '15 schools, 8,700 students',
  description: 'Students learned forgiveness practices, creating peer mediation programs that spread to neighboring schools across the region.',
  category: 'Youth Transformation'
},
{
  id: 3,
  title: 'Workplace Harmony in Mumbai',
  location: 'Mumbai, India',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1478bcc45-1764651525877.png",
  alt: 'Diverse business professionals in modern office collaborating around table with laptops',
  impact: '76% improvement in team collaboration',
  participants: '42 companies, 12,000 employees',
  description: 'Corporate chapters integrated no-enemy principles into workplace culture, reducing conflicts and increasing productivity significantly.',
  category: 'Organizational Change'
},
{
  id: 4,
  title: 'Refugee Integration in Berlin',
  location: 'Berlin, Germany',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7e208b3-1770106677165.png",
  alt: 'Multicultural group of adults sharing meal together at outdoor community gathering',
  impact: '83% increase in social cohesion',
  participants: '1,800 refugees and locals',
  description: 'Shared forgiveness circles bridged cultural divides, creating lasting friendships and mutual support networks between newcomers and residents.',
  category: 'Cultural Integration'
}];


interface TransformationStoriesProps {
  onViewStory: (storyId: number) => void;
}

const TransformationStories = ({ onViewStory }: TransformationStoriesProps) => {
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-headline font-bold text-foreground mb-3">
          Community Transformation Stories
        </h2>
        <p className="text-base font-body text-muted-foreground max-w-3xl mx-auto">
          Real communities experiencing measurable peace through the no-enemy movement
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {mockStories.map((story) =>
        <div
          key={story.id}
          className="bg-card rounded-lg shadow-md overflow-hidden border border-border hover:shadow-lg transition-all duration-300">

            <div className="relative h-64 overflow-hidden">
              <AppImage
              src={story.image}
              alt={story.alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />

              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-cta font-bold rounded-full">
                  {story.category}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  {story.title}
                </h3>
                <div className="flex items-center space-x-2 text-sm font-body text-muted-foreground">
                  <Icon name="MapPinIcon" size={16} />
                  <span>{story.location}</span>
                </div>
              </div>

              <p className="text-sm font-body text-foreground leading-relaxed">
                {story.description}
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-xs font-body text-muted-foreground mb-1">Impact</p>
                  <p className="text-sm font-body font-semibold text-success">{story.impact}</p>
                </div>
                <div>
                  <p className="text-xs font-body text-muted-foreground mb-1">Participants</p>
                  <p className="text-sm font-body font-semibold text-primary">
                    {story.participants}
                  </p>
                </div>
              </div>

              <button
              onClick={() => onViewStory(story.id)}
              className="w-full mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center space-x-2">

                <span>Read Full Story</span>
                <Icon name="ArrowRightIcon" size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Your Story CTA */}
      <div className="text-center mt-8">
        <button
          onClick={() => setShowSubmissionForm(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-md">

          <Icon name="PlusCircleIcon" size={20} />
          <span>Share Your Transformation Story</span>
        </button>
      </div>

      {/* Story Submission Modal */}
      {showSubmissionForm &&
      <StorySubmissionForm onClose={() => setShowSubmissionForm(false)} />
      }
    </div>);

};

export default TransformationStories;